import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  ArrowLeft, Shield, Lock, Eye, Clock, Trash2, Server, 
  Key, AlertTriangle, CheckCircle, XCircle, Info, Bell, UserCheck,
  Mail, ShieldCheck, Timer
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function PrivacyPolicy({ onBack }) {
  const { t } = useLanguage();

  const sections = [
    {
      id: 'intro',
      icon: Shield,
      title: t('privacy_intro_title') || 'Your Privacy Matters',
      content: t('privacy_intro_content') || 
        'Spiritual Gems is committed to protecting your privacy and securing your personal data. This policy explains how we collect, use, and protect your information.'
    },
    {
      id: 'data_collection',
      icon: Server,
      title: t('privacy_data_title') || 'Data We Collect',
      items: [
        t('privacy_data_1') || 'Account information (username, pseudonym)',
        t('privacy_data_2') || 'Profile photo (optional)',
        t('privacy_data_3') || 'Spiritual Gems you post and comments',
        t('privacy_data_4') || 'Private messages (encrypted)',
        t('privacy_data_5') || 'Bible bookmarks and notes',
        t('privacy_data_6') || 'App preferences (language, theme)',
        t('privacy_data_7') || 'Push notification preferences',
      ]
    },
    {
      id: 'auth',
      icon: UserCheck,
      title: t('security_auth_title') || 'Authentication Security',
      color: 'text-green-500',
      items: [
        {
          title: t('security_username_only') || 'Username-Only Registration',
          desc: t('security_username_only_desc') || 'No email address is required. Your privacy is maximized with username-only accounts. Usernames are validated (3-20 alphanumeric characters).'
        },
        {
          title: t('security_password') || 'Secure Password Storage (Argon2id)',
          desc: t('security_password_desc') || 'All passwords (users and admin) are hashed using Argon2id with a unique salt per entry. Argon2id is the winner of the Password Hashing Competition and is resistant to GPU and ASIC attacks.'
        },
        {
          title: t('security_jwt') || 'JWT Authentication',
          desc: t('security_jwt_desc') || 'Secure token-based authentication with 72-hour expiration. Tokens are signed and verified on each request.'
        },
      ]
    },
    {
      id: 'security_advantages',
      icon: CheckCircle,
      title: t('privacy_advantages_title') || 'Message Security',
      color: 'text-green-500',
      items: [
        {
          title: t('security_encryption_rest') || 'Encryption at Rest (AES-256-GCM)',
          desc: t('security_encryption_rest_desc') || 'All private messages are encrypted before being stored in our database. Even if the database is compromised, messages remain unreadable without the encryption key.'
        },
        {
          title: t('security_e2e') || 'End-to-End Encryption (X25519 + NaCl)',
          desc: t('security_e2e_desc') || "Messages are encrypted in your browser before being sent. The server stores only the encrypted payload and cannot read your messages."
        },
        {
          title: t('security_zero_knowledge') || 'Zero-Knowledge Architecture',
          desc: t('security_zero_knowledge_desc') || 'Your private encryption key is generated and stored exclusively in your browser (IndexedDB). It is NEVER sent to or stored on our server. Even our administrators cannot decrypt your messages.'
        },
        {
          title: t('security_pfs') || 'Perfect Forward Secrecy (PFS)',
          desc: t('security_pfs_desc') || 'Each message uses a unique ephemeral key pair. Past and future conversations remain protected even if a long-term key is compromised.'
        },
        {
          title: t('security_key_separation') || 'Client-Side Key Storage',
          desc: t('security_key_separation_desc') || 'Private keys are stored in your browser IndexedDB, never on the server. Only the public key is shared with the server for message routing.'
        },
        {
          title: t('security_argon2') || 'Argon2id Hashing',
          desc: t('security_argon2_desc') || 'Administrative passwords are protected with Argon2id — resistant to GPU and ASIC attacks.'
        },
        {
          title: t('security_secure_translation') || 'Secure Translation Pipeline',
          desc: t('security_secure_translation_desc') || 'When translating messages, plaintext exists only in server RAM. It is never logged, cached to disk, or written to any file.'
        },
        {
          title: t('security_rate_limit') || 'Rate Limiting',
          desc: t('security_rate_limit_desc') || 'Limited to 10 messages per minute to prevent spam and abuse. Admin login is also rate-limited to prevent brute-force attacks.'
        },
        {
          title: t('security_xss') || 'XSS Protection',
          desc: t('security_xss_desc') || 'All user input (messages, gems, comments) is sanitized to prevent cross-site scripting attacks. Malicious scripts are automatically stripped.'
        },
        {
          title: t('security_input_validation') || 'Input Validation',
          desc: t('security_input_validation_desc') || 'All user content is length-limited and sanitized. Gems are limited to 2000 characters, comments to 1000 characters.'
        },
        {
          title: t('security_expiration') || 'Message Expiration',
          desc: t('security_expiration_desc') || 'Messages automatically delete after 30 days. You can also manually delete your messages at any time.'
        },
        {
          title: t('security_https') || 'HTTPS Only',
          desc: t('security_https_desc') || 'All data transmission is encrypted using TLS/SSL. Your data is protected in transit.'
        },
        {
          title: t('security_ddos') || 'DDoS Protection',
          desc: t('security_ddos_desc') || 'Multi-layer protection: progressive slowdown at 80 req/min, hard block at 120 req/min, auto-ban at 300 req/min (10 min). Heavy endpoints (Bible download, translation) are limited to 10 req/min. Security headers (X-Frame-Options, X-Content-Type-Options) prevent clickjacking and MIME attacks.'
        },
      ]
    },
    {
      id: 'notifications',
      icon: Bell,
      title: t('security_notifications_title') || 'Push Notifications',
      color: 'text-blue-500',
      items: [
        {
          title: t('security_notif_browser') || 'Browser Notifications',
          desc: t('security_notif_browser_desc') || 'You can enable browser push notifications for new messages and comments on your gems. Permission is requested only once and can be revoked at any time in your browser settings.'
        },
        {
          title: t('security_notif_sound') || 'Notification Sounds & Vibration',
          desc: t('security_notif_sound_desc') || 'A gentle two-tone chime and vibration pattern alert you on mobile devices. These only trigger when new notifications arrive, not on initial page load.'
        },
        {
          title: t('security_notif_data') || 'Notification Data',
          desc: t('security_notif_data_desc') || 'Notifications are stored server-side and linked to your user ID. They contain sender name and a brief description, but not message content. Notifications do not contain any encrypted message data.'
        },
      ]
    },
    {
      id: 'security_limitations',
      icon: AlertTriangle,
      title: t('privacy_limitations_title') || 'Security Limitations',
      color: 'text-amber-500',
      items: [
        {
          title: t('limitation_server') || 'Translation Requires Temporary Plaintext',
          desc: t('limitation_server_desc') || 'To translate messages, the client sends decrypted text to the server temporarily (RAM-only, No-Trace). The server cannot proactively decrypt messages — only you can initiate translation.'
        },
        {
          title: t('limitation_metadata') || 'Metadata Visibility',
          desc: t('limitation_metadata_desc') || 'We can see who messages whom and when (metadata), but not the content. Message timestamps, sender/recipient IDs are not encrypted.'
        },
        {
          title: t('limitation_backup') || 'Database Backups',
          desc: t('limitation_backup_desc') || 'Database backups may contain encrypted messages. Old backups could persist after you delete messages.'
        },
        {
          title: t('limitation_device') || 'Device Security',
          desc: t('limitation_device_desc') || 'If your device is compromised, attackers could see decrypted messages on your screen. We cannot protect against device-level attacks.'
        },
        {
          title: t('limitation_social') || 'Social Engineering',
          desc: t('limitation_social_desc') || 'We cannot protect against phishing or social engineering attacks. Never share your password with anyone.'
        },
      ]
    },
    {
      id: 'your_rights',
      icon: Key,
      title: t('privacy_rights_title') || 'Your Rights',
      items: [
        t('privacy_rights_1') || 'Access your personal data at any time',
        t('privacy_rights_2') || 'Delete your messages and content',
        t('privacy_rights_3') || 'Update or correct your profile information',
        t('privacy_rights_4') || 'Disable private messaging to stop receiving messages',
        t('privacy_rights_5') || 'Request account deletion (contact us)',
        t('privacy_rights_6') || 'Control push notification permissions in your browser',
      ]
    },
    {
      id: 'data_retention',
      icon: Clock,
      title: t('privacy_retention_title') || 'Data Retention',
      content: t('privacy_retention_content') || 
        'Private messages are automatically deleted after 30 days. Notifications are kept until read or cleared. Spiritual Gems and comments are kept until you delete them. Account data is retained until you request account deletion.'
    },
    {
      id: 'third_party',
      icon: Eye,
      title: t('privacy_third_party_title') || 'Third-Party Services',
      items: [
        t('privacy_third_party_1') || 'Microsoft Azure Translator (optional, for auto-translation)',
        t('privacy_third_party_2') || 'JW.org (Bible content and audio)',
        t('privacy_third_party_3') || 'PayPal (donation processing, external link)',
      ],
      note: t('privacy_third_party_note') || 'Donations are processed externally via PayPal; Spiritual Gems never sees or stores your financial data. Azure translations follow a strict No-Trace policy — content exists only in RAM and is never logged.'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="min-h-screen pb-24"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-full hover:bg-accent transition-colors"
            data-testid="privacy-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-semibold">{t('privacy_policy') || 'Privacy & Security'}</h1>
            <p className="text-xs text-muted-foreground">{t('privacy_subtitle') || 'Security & Data Protection'}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        {/* Security at a Glance */}
        <div className="rounded-xl bg-green-500/10 border border-green-500/20 p-5" data-testid="security-at-a-glance">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-green-500" />
            <h2 className="text-sm font-bold text-green-600 dark:text-green-400">
              {t('security_at_a_glance') || 'Security at a Glance'}
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center shrink-0">
                <UserCheck className="w-4 h-4 text-green-500" />
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground">{t('glance_no_email') || 'No Email Required'}</p>
                <p className="text-[10px] text-muted-foreground">{t('glance_no_email_desc') || 'Total privacy — username-only accounts'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center shrink-0">
                <Lock className="w-4 h-4 text-green-500" />
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground">{t('glance_encryption') || 'Zero-Knowledge Encryption'}</p>
                <p className="text-[10px] text-muted-foreground">{t('glance_encryption_desc') || 'X25519 + NaCl — keys stay in your browser'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center shrink-0">
                <Timer className="w-4 h-4 text-green-500" />
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground">{t('glance_auto_delete') || 'Auto-Deletion'}</p>
                <p className="text-[10px] text-muted-foreground">{t('glance_auto_delete_desc') || 'Messages vanish after 30 days'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sections */}
        {sections.map((section, index) => (
          <motion.div
            key={section.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="rounded-xl border border-border bg-card p-4"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-lg bg-accent ${section.color || 'text-primary'}`}>
                <section.icon className="w-5 h-5" />
              </div>
              <h2 className="text-base font-semibold">{section.title}</h2>
            </div>

            {section.content && (
              <p className="text-sm text-muted-foreground leading-relaxed">
                {section.content}
              </p>
            )}

            {section.items && (
              <ul className="space-y-2">
                {section.items.map((item, i) => (
                  <li key={i} className="text-sm">
                    {typeof item === 'string' ? (
                      <div className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span className="text-muted-foreground">{item}</span>
                      </div>
                    ) : (
                      <div className="ml-2 p-3 rounded-lg bg-accent/50 mb-2">
                        <div className="flex items-center gap-2 mb-1">
                          {section.color === 'text-amber-500' ? (
                            <XCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          ) : section.color === 'text-blue-500' ? (
                            <Bell className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          )}
                          <span className="font-medium text-foreground">{item.title}</span>
                        </div>
                        <p className="text-xs text-muted-foreground ml-6">{item.desc}</p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {section.note && (
              <div className="mt-3 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-600 dark:text-blue-400">{section.note}</p>
                </div>
              </div>
            )}
          </motion.div>
        ))}

        {/* Last Updated */}
        <div className="text-center text-xs text-muted-foreground pt-4">
          <p>{t('privacy_last_updated') || 'Last updated'}: March 10, 2026</p>
          <p className="mt-1">{t('privacy_contact') || 'Questions? Use "Suggest a modification" to contact us.'}</p>
        </div>
      </div>
    </motion.div>
  );
}
